/**
 * Query history panel component.
 * Displays executed queries from localStorage.
 * Inspired by DBeaver's Query Manager.
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { QueryHistoryItem } from '../types';
import { getQueryHistory, clearQueryHistory, saveQueryHistory } from '../services/storage';

interface QueryHistoryProps {
  onSelectQuery: (sql: string) => void;
  refreshTrigger?: number;
}

const QueryHistory: React.FC<QueryHistoryProps> = ({ onSelectQuery, refreshTrigger }) => {
  const [history, setHistory] = React.useState<QueryHistoryItem[]>([]);

  React.useEffect(() => {
    loadHistory();
  }, [refreshTrigger]);

  const loadHistory = () => {
    const items = getQueryHistory();
    setHistory(items.reverse()); // Show most recent first
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all query history?')) {
      clearQueryHistory();
      loadHistory();
    }
  };

  const handleDeleteItem = (id: string) => {
    const updated = getQueryHistory().filter(item => item.id !== id);
    saveQueryHistory(updated);
    loadHistory();
  };

  const handleCopyQuery = (sql: string) => {
    navigator.clipboard.writeText(sql);
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const truncateSql = (sql: string, maxLength: number = 100): string => {
    if (sql.length <= maxLength) return sql;
    return sql.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight="bold">
          Query History
        </Typography>
        {history.length > 0 && (
          <IconButton size="small" onClick={handleClearAll} title="Clear All">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {history.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No query history yet
            </Typography>
          </Box>
        ) : (
          <List dense>
            {history.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
                onClick={() => onSelectQuery(item.sql)}
                secondaryAction={
                  <Box>
                    <Tooltip title="Copy SQL">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyQuery(item.sql);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteItem(item.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: item.status === 'error' ? 'error.main' : 'text.primary',
                      }}
                    >
                      {truncateSql(item.sql)}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(item.timestamp)} • {item.executionTime}ms
                      {item.status === 'error' && ' • Failed'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default QueryHistory;





